import iconLandmark from '@/assets/pin备份 9@2x.png'

export type TasksMapPanelTab = 'inspectors' | 'technician'

export type TasksMapCategoryKey = 'essential' | 'enhanced' | 'premium' | 'followup' | 'addon'

export interface TasksMapPoint {
  lat: number
  lng: number
}

export interface TasksMapOrder {
  id: string
  category: TasksMapCategoryKey
  address: string
  orderId: string
  time: string
  position: TasksMapPoint
  sequence: number
}

export interface TasksMapPerson {
  id: string
  name: string
  initials: string
  color: string
  orderCount: number
  position: TasksMapPoint
  orders: TasksMapOrder[]
}

/** Backend may omit color — assigned automatically per person. */
export type TasksMapPersonInput = Omit<TasksMapPerson, 'color' | 'initials' | 'orderCount'> & {
  color?: string
  initials?: string
  orderCount?: number
}

/** Palette cycles for any number of people; same id always maps to the same color. */
export const TASKS_MAP_PERSON_COLORS = [
  '#d9f0ff',
  '#00aeef',
  '#b8e4ff',
  '#98d5be',
  '#ffc8c8',
  '#ffe4b8',
  '#c8e6ff',
  '#d4f4dd',
  '#e8d4ff',
  '#ffd9ec',
] as const

function hashPersonId(id: string): number {
  let hash = 0
  for (let i = 0; i < id.length; i++) {
    hash = (hash + id.charCodeAt(i) * (i + 1)) % 2147483647
  }
  return Math.abs(hash)
}

export function getTasksMapPersonColor(personId: string, fallbackIndex = 0): string {
  const palette = TASKS_MAP_PERSON_COLORS
  if (!personId) {
    return palette[fallbackIndex % palette.length]!
  }
  return palette[hashPersonId(personId) % palette.length]!
}

export function isTasksMapLightColor(color: string): boolean {
  const hex = color.replace('#', '')
  if (hex.length !== 6) return false
  const r = Number.parseInt(hex.slice(0, 2), 16)
  const g = Number.parseInt(hex.slice(2, 4), 16)
  const b = Number.parseInt(hex.slice(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.7
}

function deriveInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return `${parts[0]![0] ?? ''}${parts[parts.length - 1]![0] ?? ''}`.toUpperCase()
}

export function normalizeTasksMapPerson(person: TasksMapPersonInput, index = 0): TasksMapPerson {
  const orders = person.orders ?? []
  return {
    ...person,
    initials: person.initials?.trim() || deriveInitials(person.name),
    orderCount: person.orderCount ?? orders.length,
    orders,
    color: person.color?.trim() || getTasksMapPersonColor(person.id, index),
  }
}

export function normalizeTasksMapPeople(people: TasksMapPersonInput[]): TasksMapPerson[] {
  return people.map((person, index) => normalizeTasksMapPerson(person, index))
}

export function getTasksMapLandmarkMaskStyle(color: string): Record<string, string> {
  return {
    WebkitMaskImage: `url(${iconLandmark})`,
    maskImage: `url(${iconLandmark})`,
    backgroundColor: color,
  }
}
